"use client";

import { c as _c } from "react/compiler-runtime";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { useContext, useEffect, useEffectEvent, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import DatePickerInput from "@/app/(main)/(club)/mes-clubs/[id]/components/date-picker-input";
import InvitedList from "@/app/(main)/(club)/mes-clubs/[id]/components/invited-list";
import { fetchEditEvents } from "@/app/(main)/(club)/mes-clubs/[id]/utils/fetchValues";
import { HonoredMemberOption, mergePresidentIntoHonoredOptions } from "@/app/(main)/(club)/mes-clubs/[id]/utils/honored-member-options";
import { getClubEvent, updateEventClub } from "@/app/api/dynabuy-back/api-events";
import Button from "@/components/button";
import GlobalError from "@/components/errors/global-error";
import ClientFlash from "@/components/global/client-flash";
import ComponentLoading from "@/components/global/component-loading";
import HocTextInput from "@/components/global/inputs/hoc-text-input";
import SelectInputContainer from "@/components/global/inputs/select-input-container";
import TextInput from "@/components/global/inputs/text-input";
import TimePickerContainer from "@/components/global/inputs/time-picker-container";
import MainTitle from "@/components/global/main-title";
import AddressAutocompleteInput, { AddressFeature } from "@/components/form/inputs/address-autocomplete-input";
import { sidebarContext } from "@/context/sidebar-context";
import useSubmit from "@/hooks/use-submit";
import { useClubMembers } from "@/queries/club/use-club-members";
import { useModalStore } from "@/stores/modal-store";
import { EventForCalendar, EventLocalObject, EventRead, Person } from "@/TS/Interfaces/global_interfaces";
import { EventTypeTitleText } from "@/TS/KeyMappings/global_key_mappings";
import { handleErrors } from "@/utils/formFunc/form-func";
const _dayjs = (date: dayjs.ConfigType, c?: string) => {
  if (typeof date === "string") date = date.replace(/-/g, "/");
  return dayjs(date, c);
};
function EditEvent(t0) {
  const $ = _c(104);
  const {
    event,
    arrOfEvents,
    handleMonthDate
  } = t0;
  const {
    close
  } = useModalStore();
  const queryClient = useQueryClient();
  const {
    data: clubMembers
  } = useClubMembers(event.clubSlug);
  let t1;
  if ($[0] !== clubMembers) {
    t1 = clubMembers?.find(_temp);
    $[0] = clubMembers;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const presidentMember = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      defaultValues: {
        agenda: "",
        endDate: _dayjs(new Date()),
        startDate: _dayjs(new Date()),
        postalAddress: {
          country: "",
          city: "",
          zipCode: "",
          street: "",
          latitude: undefined,
          longitude: undefined
        },
        notifyByEmail: false
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    register,
    control,
    reset,
    setValue,
    handleSubmit
  } = useForm(t2);
  const [addressInput, setAddressInput] = useState("");
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      message: "La modification a \xE9t\xE9 appliqu\xE9e avec succ\xE8s !",
      type: "success2",
      position: "mes-clubs-calendar"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== close || $[5] !== handleMonthDate || $[6] !== queryClient) {
    t4 = {
      callbackMessage: t3,
      callback: () => {
        handleMonthDate(0);
        queryClient.invalidateQueries().then(() => close());
      },
      sidebarJsx: "",
      errorFlashPath: "/mes-clubs#edit-event"
    };
    $[4] = close;
    $[5] = handleMonthDate;
    $[6] = queryClient;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    errors,
    loading,
    onSubmit
  } = useSubmit(updateEventClub, t4);
  const [detailedEvent, setDetailedEvent] = useState(null);
  let t5;
  if ($[8] !== loading || $[9] !== register) {
    t5 = HocTextInput(TextInput, loading, register);
    $[8] = loading;
    $[9] = register;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const EnhancedTextInput = t5;
  const {
    handleJsx
  } = useContext(sidebarContext);
  let t6;
  if ($[11] !== setValue) {
    t6 = feature => {
      setValue("postalAddress.street", feature.properties.name || feature.properties.street || "");
      setValue("postalAddress.zipCode", feature.properties.postcode);
      setValue("postalAddress.city", feature.properties.city);
      setValue("postalAddress.latitude", feature.geometry.coordinates[1]);
      setValue("postalAddress.longitude", feature.geometry.coordinates[0]);
    };
    $[11] = setValue;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleAddressSelect = t6;
  let t7;
  if ($[13] !== event.clubSlug || $[14] !== event.startDate || $[15] !== event.type || $[16] !== reset) {
    t7 = () => {
      const fetchClub = async () => {
        const clubEvent = await getClubEvent(event.type, event.clubSlug, event.startDate.split("T")[0]);
        if (clubEvent) {
          setDetailedEvent(clubEvent);
          reset({
            agenda: clubEvent.agenda,
            endDate: _dayjs(new Date(clubEvent.endDate)),
            absentMembers: clubEvent.absentMembers,
            honoredMembers: clubEvent.honoredMembers,
            startDate: _dayjs(new Date(clubEvent.startDate)),
            postalAddress: {
              city: clubEvent.postalAddress.city,
              street: clubEvent.postalAddress.street,
              zipCode: clubEvent.postalAddress.zipCode,
              place: clubEvent.postalAddress.place,
              latitude: clubEvent.postalAddress.latitude ? parseFloat(String(clubEvent.postalAddress.latitude)) : undefined,
              longitude: clubEvent.postalAddress.longitude ? parseFloat(String(clubEvent.postalAddress.longitude)) : undefined
            },
            notifyByEmail: clubEvent.notifyByEmail
          }, {
            keepErrors: false
          });
          setAddressInput([clubEvent.postalAddress.street, clubEvent.postalAddress.zipCode, clubEvent.postalAddress.city].filter(Boolean).join(" "));
        }
      };
      fetchClub();
    };
    $[13] = event.clubSlug;
    $[14] = event.startDate;
    $[15] = event.type;
    $[16] = reset;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const loadDetailedEvent = useEffectEvent(t7);
  let t8;
  if ($[18] !== loadDetailedEvent) {
    t8 = () => {
      loadDetailedEvent();
    };
    $[18] = loadDetailedEvent;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  useEffect(t8, t9);
  if (!detailedEvent) {
    let t10;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <ComponentLoading />;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    return t10;
  }
  let t10;
  if ($[22] !== event.clubSlug || $[23] !== event.startDate || $[24] !== event.type || $[25] !== onSubmit) {
    t10 = data => {
      onSubmit(fetchEditEvents(data), event.type, event.clubSlug, event.startDate.split("T")[0]);
    };
    $[22] = event.clubSlug;
    $[23] = event.startDate;
    $[24] = event.type;
    $[25] = onSubmit;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== handleSubmit || $[28] !== t10) {
    t11 = handleSubmit(t10);
    $[27] = handleSubmit;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== event.type) {
    t12 = EventTypeTitleText.get(event.type);
    $[30] = event.type;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t12) {
    t13 = <MainTitle className="text-center w-full" title={t12} />;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== arrOfEvents || $[35] !== event || $[36] !== loading || $[37] !== register || $[38] !== setValue) {
    t14 = <DatePickerInput setValue={setValue} event={event} arrOfEvents={arrOfEvents} loading={loading} register={register} />;
    $[34] = arrOfEvents;
    $[35] = event;
    $[36] = loading;
    $[37] = register;
    $[38] = setValue;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== errors) {
    t15 = t16 => {
      const {
        field: t17
      } = t16;
      const {
        ref: _ref,
        ...rest
      } = t17;
      return <TimePickerContainer className="w-1/2" label="HEURE DE DEBUT" error={handleErrors(errors, "startDate")} {...rest} />;
    };
    $[40] = errors;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== control || $[43] !== t15) {
    t16 = <Controller control={control} name="startDate" render={t15} />;
    $[42] = control;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== errors) {
    t17 = t18 => {
      const {
        field: t19
      } = t18;
      const {
        ref: _ref_0,
        ...rest_0
      } = t19;
      return <TimePickerContainer className="w-1/2" label="HEURE DE FIN" error={handleErrors(errors, "endDate")} {...rest_0} />;
    };
    $[45] = errors;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== control || $[48] !== t17) {
    t18 = <Controller control={control} name="endDate" render={t17} />;
    $[47] = control;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t16 || $[51] !== t18) {
    t19 = <div className="flex gap-8">{t16}{t18}</div>;
    $[50] = t16;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== EnhancedTextInput) {
    t20 = <EnhancedTextInput labelClassName="font-bold" name="postalAddress.place" needed={false} label="LIEU" />;
    $[53] = EnhancedTextInput;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== addressInput || $[56] !== handleAddressSelect || $[57] !== loading) {
    t21 = <AddressAutocompleteInput value={addressInput} onChange={setAddressInput} onSelect={handleAddressSelect} label="ADRESSE" disabled={loading} />;
    $[55] = addressInput;
    $[56] = handleAddressSelect;
    $[57] = loading;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== EnhancedTextInput) {
    t22 = <EnhancedTextInput labelClassName="font-bold" name="agenda" needed={false} label="ORDRE DU JOUR" />;
    $[59] = EnhancedTextInput;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== detailedEvent?.attendedMembers || $[62] !== presidentMember) {
    t23 = t24 => {
      const {
        field: t25
      } = t24;
      const {
        ref: _ref_1,
        value,
        ...rest_1
      } = t25;
      return <SelectInputContainer val={value} defaultValues={value.map(_temp2)} options={mergePresidentIntoHonoredOptions(detailedEvent?.attendedMembers, presidentMember ? {
        "@id": presidentMember["@id"],
        firstName: presidentMember.firstName,
        lastName: presidentMember.lastName
      } : null)} label={"MEMBRE(S) MIS \xC0 L'HONNEUR (maximum 2)"} {...rest_1} />;
    };
    $[61] = detailedEvent?.attendedMembers;
    $[62] = presidentMember;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== control || $[65] !== t23) {
    t24 = <Controller control={control} name="honoredMembers" render={t23} />;
    $[64] = control;
    $[65] = t23;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  let t25;
  if ($[67] !== detailedEvent) {
    t25 = <div className="flex flex-col md:flex-row justify-between gap-0 md:gap-8"><InvitedList event={detailedEvent} title={"Invit\xE9s en attente"} type="requestsInProcess" defaultMessage={"Aucun invit\xE9"} /><InvitedList event={detailedEvent} title={"Invit\xE9s confirm\xE9s"} type="requestsValidated" defaultMessage={"Aucun invit\xE9"} /></div>;
    $[67] = detailedEvent;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  let t26;
  if ($[69] !== control) {
    t26 = <div className="flex flex-col"><Controller name="notifyByEmail" control={control} render={_temp3} /></div>;
    $[69] = control;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== errors) {
    t27 = handleErrors(errors, "");
    $[71] = errors;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== t27) {
    t28 = <GlobalError error={t27} />;
    $[73] = t27;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <ClientFlash path="/mes-clubs#edit-event" />;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== close || $[77] !== handleJsx) {
    t30 = () => {
      close();
      handleJsx();
    };
    $[76] = close;
    $[77] = handleJsx;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  let t31;
  if ($[79] !== loading || $[80] !== t30) {
    t31 = <Button colorTheme="secondary" variant="unfilled" className="md:min-w-full w-full py-3" type="button" disabled={loading} onClick={t30}>Annuler</Button>;
    $[79] = loading;
    $[80] = t30;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  let t32;
  if ($[82] !== loading) {
    t32 = <Button colorTheme="primary" variant="filled" className="md:min-w-full w-full py-3" type="submit" disabled={loading}>Valider</Button>;
    $[82] = loading;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  let t33;
  if ($[84] !== t31 || $[85] !== t32) {
    t33 = <div className="flex flex-col-reverse md:flex-row self-center items-center md:w-56 w-full justify-center gap-6">{t31}{t32}</div>;
    $[84] = t31;
    $[85] = t32;
    $[86] = t33;
  } else {
    t33 = $[86];
  }
  let t34;
  if ($[87] !== t14 || $[88] !== t19 || $[89] !== t20 || $[90] !== t21 || $[91] !== t22 || $[92] !== t24 || $[93] !== t25 || $[94] !== t26 || $[95] !== t28 || $[96] !== t33) {
    t34 = <div className="flex flex-col p-4 gap-6 rounded-lg">{t14}{t19}{t20}{t21}{t22}{t24}{t25}{t26}{t28}{t29}{t33}</div>;
    $[87] = t14;
    $[88] = t19;
    $[89] = t20;
    $[90] = t21;
    $[91] = t22;
    $[92] = t24;
    $[93] = t25;
    $[94] = t26;
    $[95] = t28;
    $[96] = t33;
    $[97] = t34;
  } else {
    t34 = $[97];
  }
  let t35;
  if ($[98] !== t13 || $[99] !== t34) {
    t35 = <div className="flex flex-col">{t13}{t34}</div>;
    $[98] = t13;
    $[99] = t34;
    $[100] = t35;
  } else {
    t35 = $[100];
  }
  let t36;
  if ($[101] !== t11 || $[102] !== t35) {
    t36 = <LocalizationProvider dateAdapter={AdapterDayjs}><form className="bg-white flex flex-col p-4 gap-6 rounded-lg md:w-3/5 w-full mx-auto z-50" onSubmit={t11}>{t35}</form></LocalizationProvider>;
    $[101] = t11;
    $[102] = t35;
    $[103] = t36;
  } else {
    t36 = $[103];
  }
  return t36;
}
function _temp3(t0) {
  const {
    field: t1
  } = t0;
  const {
    ref: _ref_2,
    value: value_0,
    ...rest_2
  } = t1;
  return <FormControlLabel control={<Checkbox checked={!!value_0} onChange={e => rest_2.onChange(e.target.checked)} sx={{
    borderRadius: "50%",
    "& .MuiSvgIcon-root": {
      borderRadius: "50%"
    },
    "&.Mui-checked": {
      color: "#EE4540",
      "& .MuiSvgIcon-root": {
        borderRadius: "50%"
      }
    }
  }} />} label={<span className="italic text-sm">Notifier les membres par email</span>} />;
}
function _temp2(member_0) {
  return {
    value: member_0["@id"],
    label: member_0.firstName + " " + member_0.lastName
  };
}
function _temp(member) {
  return member.isPresident;
}
export default EditEvent;